import { useRef, type HTMLAttributes, type MouseEvent } from 'react'
import { cn } from '@/lib/utils'

type SpotlightCardProps = HTMLAttributes<HTMLDivElement> & {
  /** RGB triplet for the cursor glow, e.g. "196 181 253" */
  glow?: string
  /** Layout classes for the content wrapper (grid / flex) */
  contentClassName?: string
}

/**
 * Card with a soft radial glow that follows the pointer and a lit border.
 * Pure CSS variables — no re-renders on mousemove.
 */
export function SpotlightCard({
  className,
  contentClassName,
  children,
  glow = '196 181 253',
  style,
  ...props
}: SpotlightCardProps) {
  const ref = useRef<HTMLDivElement>(null)

  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    el.style.setProperty('--x', `${e.clientX - r.left}px`)
    el.style.setProperty('--y', `${e.clientY - r.top}px`)
  }

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      style={{ ...style, ['--glow' as string]: glow }}
      className={cn(
        'group/spot relative overflow-hidden rounded-3xl border border-white/[0.08] bg-white/[0.025] backdrop-blur-xl transition-colors duration-500 hover:border-white/[0.14]',
        className,
      )}
      {...props}
    >
      {/* pointer glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-500 group-hover/spot:opacity-100"
        style={{
          background:
            'radial-gradient(420px circle at var(--x, 50%) var(--y, 50%), rgb(var(--glow) / 0.12), transparent 45%)',
        }}
      />
      {/* top hairline highlight */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"
      />
      <div className={cn('relative h-full', contentClassName)}>{children}</div>
    </div>
  )
}
